import React, { useState, useEffect, useCallback } from "react";
import api, { formatVND } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Video, Camera, FileText, CalendarDays, Megaphone, Loader2, Plus,
  Trash2, Pencil, Eye, Sparkles, Film, Mic, Lightbulb, Clock,
  Target, Clapperboard, ChevronRight, Copy, RefreshCw, Star,
  BarChart3, ClipboardList, Image, TrendingUp, Heart, MessageSquare,
  Share2, Users, DollarSign, CheckCircle, ArrowRight,
} from "lucide-react";

const SUB_TABS = [
  { key: "video-planner", label: "Video Planner", icon: Film },
  { key: "content-ideas", label: "Ý tưởng", icon: Lightbulb },
  { key: "equipment", label: "Thiết bị", icon: Camera },
  { key: "scripts", label: "Kịch bản", icon: FileText },
  { key: "calendar", label: "Lịch content", icon: CalendarDays },
  { key: "campaigns", label: "Chiến dịch", icon: Megaphone },
  { key: "tasks", label: "Tasks", icon: ClipboardList },
  { key: "performance", label: "Hiệu suất", icon: BarChart3 },
];

export default function MarketingPanel() {
  const [subTab, setSubTab] = useState("video-planner");

  return (
    <div className="flex-1 overflow-hidden flex flex-col" data-testid="marketing-panel">
      {/* Sub-tabs */}
      <div className="flex gap-1 px-4 py-2 border-b border-[#1e293b] bg-[#0a0f1a] overflow-x-auto">
        {SUB_TABS.map(t => (
          <button key={t.key} onClick={() => setSubTab(t.key)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium whitespace-nowrap transition-colors ${
              subTab === t.key ? "bg-[#1e293b] text-lime-400" : "text-slate-500 hover:text-slate-300"
            }`} data-testid={`mkt-tab-${t.key}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto">
        {subTab === "video-planner" && <VideoPlannerTab />}
        {subTab === "content-ideas" && <ContentIdeasTab />}
        {subTab === "equipment" && <EquipmentTab />}
        {subTab === "scripts" && <ScriptsTab />}
        {subTab === "calendar" && <CalendarTab />}
        {subTab === "campaigns" && <CampaignsTab />}
        {subTab === "tasks" && <ProductionTasksTab />}
        {subTab === "performance" && <PerformanceTab />}
      </div>
    </div>
  );
}

// ==================== VIDEO PLANNER ====================
function VideoPlannerTab() {
  const [form, setForm] = useState({ idea: "", goal: "awareness", target_audience: "", platform: "TikTok", duration: "30s", location: "", people: "", tone: "vui vẻ", cta: "" });
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState(null);
  const [planTab, setPlanTab] = useState("concept");

  const generate = async () => {
    if (!form.idea.trim()) { toast.error("Nhập ý tưởng video"); return; }
    setLoading(true);
    setPlan(null);
    try {
      const { data } = await api.post("/marketing/video-plan", form);
      setPlan(data.plan);
      toast.success("Tạo kế hoạch thành công!");
    } catch (err) { toast.error(err.response?.data?.detail || "Lỗi tạo kế hoạch"); }
    finally { setLoading(false); }
  };

  const PLAN_TABS = [
    { key: "concept", label: "Ý tưởng" },
    { key: "scenes", label: "Bối cảnh" },
    { key: "shots", label: "Shot list" },
    { key: "script", label: "Kịch bản" },
    { key: "equipment", label: "Thiết bị" },
    { key: "checklist", label: "Checklist" },
    { key: "editing", label: "Dựng" },
    { key: "publishing", label: "Đăng bài" },
  ];

  return (
    <div className="p-4 md:p-6 space-y-4 max-w-5xl mx-auto" data-testid="video-planner">
      {!plan ? (
        <>
          <div className="flex items-center gap-2 mb-2">
            <Clapperboard className="w-5 h-5 text-lime-400" />
            <h2 className="text-lg font-bold text-slate-200">AI tạo kế hoạch video</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <Label className="text-slate-400 text-xs">Ý tưởng video *</Label>
              <Input value={form.idea} onChange={e => setForm({...form, idea: e.target.value})}
                placeholder="VD: Video giới thiệu sân pickleball buổi tối, có nhóm bạn chơi social match"
                className="bg-[#1e293b] border-[#334155] text-slate-200 mt-1" data-testid="video-idea-input" />
            </div>
            <div>
              <Label className="text-slate-400 text-xs">Mục tiêu</Label>
              <select value={form.goal} onChange={e => setForm({...form, goal: e.target.value})}
                className="w-full mt-1 bg-[#1e293b] border border-[#334155] rounded-md px-3 py-2 text-sm text-slate-200">
                <option value="awareness">Nhận diện thương hiệu</option>
                <option value="booking">Tăng đặt sân</option>
                <option value="membership">Bán membership</option>
                <option value="lead">Thu leads</option>
                <option value="event">Quảng bá sự kiện</option>
                <option value="cafe_sales">Bán cafe/shop</option>
              </select>
            </div>
            <div>
              <Label className="text-slate-400 text-xs">Nền tảng</Label>
              <select value={form.platform} onChange={e => setForm({...form, platform: e.target.value})}
                className="w-full mt-1 bg-[#1e293b] border border-[#334155] rounded-md px-3 py-2 text-sm text-slate-200">
                <option>TikTok</option>
                <option>Facebook Reels</option>
                <option>YouTube Shorts</option>
                <option>Instagram</option>
              </select>
            </div>
            <div>
              <Label className="text-slate-400 text-xs">Thời lượng</Label>
              <select value={form.duration} onChange={e => setForm({...form, duration: e.target.value})}
                className="w-full mt-1 bg-[#1e293b] border border-[#334155] rounded-md px-3 py-2 text-sm text-slate-200">
                <option value="15s">15 giây</option>
                <option value="30s">30 giây</option>
                <option value="60s">60 giây</option>
                <option value="90s">90 giây</option>
              </select>
            </div>
            <div>
              <Label className="text-slate-400 text-xs">Phong cách</Label>
              <select value={form.tone} onChange={e => setForm({...form, tone: e.target.value})}
                className="w-full mt-1 bg-[#1e293b] border border-[#334155] rounded-md px-3 py-2 text-sm text-slate-200">
                <option value="vui vẻ">Vui vẻ, năng động</option>
                <option value="chuyên nghiệp">Chuyên nghiệp</option>
                <option value="viral">Viral, trend</option>
                <option value="cảm xúc">Cảm xúc, storytelling</option>
                <option value="review">Review, đánh giá</option>
                <option value="hướng dẫn">Hướng dẫn, tutorial</option>
              </select>
            </div>
            <div>
              <Label className="text-slate-400 text-xs">Địa điểm</Label>
              <Input value={form.location} onChange={e => setForm({...form, location: e.target.value})}
                placeholder="Sân pickleball / FitxFit Gym / Cafe..."
                className="bg-[#1e293b] border-[#334155] text-slate-200 mt-1" />
            </div>
            <div>
              <Label className="text-slate-400 text-xs">Đối tượng</Label>
              <Input value={form.target_audience} onChange={e => setForm({...form, target_audience: e.target.value})}
                placeholder="Dân công sở 25-40, gia đình..."
                className="bg-[#1e293b] border-[#334155] text-slate-200 mt-1" />
            </div>
            <div>
              <Label className="text-slate-400 text-xs">Người tham gia</Label>
              <Input value={form.people} onChange={e => setForm({...form, people: e.target.value})}
                placeholder="Coach, khách hàng, staff..."
                className="bg-[#1e293b] border-[#334155] text-slate-200 mt-1" />
            </div>
            <div>
              <Label className="text-slate-400 text-xs">CTA mong muốn</Label>
              <Input value={form.cta} onChange={e => setForm({...form, cta: e.target.value})}
                placeholder="Đặt sân ngay / Inbox đăng ký..."
                className="bg-[#1e293b] border-[#334155] text-slate-200 mt-1" />
            </div>
          </div>

          <Button onClick={generate} disabled={loading}
            className="w-full bg-lime-500 hover:bg-lime-400 text-[#0f172a] font-semibold gap-2 h-11 mt-2" data-testid="generate-video-plan-btn">
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> AI đang lên kế hoạch...</> : <><Sparkles className="w-4 h-4" /> Tạo kế hoạch sản xuất</>}
          </Button>
        </>
      ) : (
        <>
          {/* Plan Result */}
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-lime-400">{plan.concept?.title || "Kế hoạch video"}</h2>
            <Button onClick={() => setPlan(null)} variant="outline" size="sm" className="border-[#334155] text-slate-400 text-xs">Tạo mới</Button>
          </div>

          {/* Plan tabs */}
          <div className="flex gap-1 flex-wrap">
            {PLAN_TABS.map(t => (
              <button key={t.key} onClick={() => setPlanTab(t.key)}
                className={`px-3 py-1.5 rounded-lg text-[11px] font-medium transition-colors ${
                  planTab === t.key ? "bg-lime-500/20 text-lime-400" : "text-slate-500 hover:text-slate-300 bg-[#1e293b]"
                }`}>{t.label}</button>
            ))}
          </div>

          <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4 min-h-[300px]">
            {planTab === "concept" && plan.concept && (
              <div className="space-y-3">
                <div><p className="text-[10px] text-slate-500 uppercase">Hooks (3 giây đầu)</p>
                  {plan.concept.hooks?.map((h, i) => <p key={i} className="text-sm text-slate-200 mt-1">• {h}</p>)}
                </div>
                <div><p className="text-[10px] text-slate-500 uppercase">Góc tiếp cận</p>
                  {plan.concept.angles?.map((a, i) => <p key={i} className="text-sm text-slate-300 mt-1">• {a}</p>)}
                </div>
                <div><p className="text-[10px] text-slate-500 uppercase">Đối tượng</p><p className="text-sm text-slate-300">{plan.concept.target_segment}</p></div>
                <div><p className="text-[10px] text-slate-500 uppercase">Kết quả kỳ vọng</p><p className="text-sm text-lime-400">{plan.concept.expected_outcome}</p></div>
              </div>
            )}
            {planTab === "scenes" && plan.scenes && (
              <div className="space-y-3">
                {plan.scenes.map((s, i) => (
                  <div key={i} className="bg-[#0f172a] rounded-lg p-3">
                    <p className="text-sm font-medium text-slate-200">{s.scene_name}</p>
                    <p className="text-xs text-slate-400 mt-1">{s.why_fits}</p>
                    <div className="flex gap-4 mt-2">
                      <span className="text-[10px] text-slate-500">Cảm xúc: <span className="text-amber-400">{s.emotion}</span></span>
                      <span className="text-[10px] text-slate-500">Thời gian: <span className="text-sky-400">{s.best_time}</span></span>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {planTab === "shots" && plan.shot_list && (
              <div className="space-y-2">
                {plan.shot_list.map((s, i) => (
                  <div key={i} className="bg-[#0f172a] rounded-lg p-3 flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-lime-500/20 flex items-center justify-center text-lime-400 text-xs font-bold shrink-0">{s.order}</div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-200">{s.shot_name}</p>
                      <div className="grid grid-cols-2 gap-1 mt-1 text-[10px]">
                        <span className="text-slate-500">Camera: <span className="text-slate-300">{s.camera_position}</span></span>
                        <span className="text-slate-500">Khung: <span className="text-slate-300">{s.framing}</span></span>
                        <span className="text-slate-500">Movement: <span className="text-slate-300">{s.movement}</span></span>
                        <span className="text-slate-500">Duration: <span className="text-lime-400">{s.duration_sec}s</span></span>
                      </div>
                      {s.voice_over && <p className="text-[10px] text-sky-400 mt-1">VO: {s.voice_over}</p>}
                      {s.on_screen_text && <p className="text-[10px] text-amber-400 mt-0.5">Text: {s.on_screen_text}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {planTab === "script" && plan.script && (
              <div className="space-y-3">
                <div><p className="text-[10px] text-slate-500 uppercase">Hook</p><p className="text-sm text-lime-400 font-medium">{plan.script.hook}</p></div>
                <div><p className="text-[10px] text-slate-500 uppercase">Nội dung chính</p><p className="text-sm text-slate-200 whitespace-pre-wrap">{plan.script.body}</p></div>
                <div><p className="text-[10px] text-slate-500 uppercase">CTA</p><p className="text-sm text-amber-400">{plan.script.cta}</p></div>
                <div><p className="text-[10px] text-slate-500 uppercase">Voice-over đầy đủ</p><p className="text-sm text-slate-300 whitespace-pre-wrap bg-[#0f172a] p-3 rounded-lg">{plan.script.full_voice_over}</p></div>
              </div>
            )}
            {planTab === "equipment" && plan.equipment && (
              <div className="space-y-3">
                <div><p className="text-[10px] text-slate-500 uppercase">Bắt buộc</p>
                  {plan.equipment.required?.map((e, i) => <Badge key={i} className="bg-red-500/20 text-red-400 text-[10px] border-0 mr-1 mt-1">{e}</Badge>)}
                </div>
                <div><p className="text-[10px] text-slate-500 uppercase">Tùy chọn</p>
                  {plan.equipment.optional?.map((e, i) => <Badge key={i} className="bg-sky-500/20 text-sky-400 text-[10px] border-0 mr-1 mt-1">{e}</Badge>)}
                </div>
                <div><p className="text-[10px] text-slate-500 uppercase">Setup</p><p className="text-sm text-slate-300">{plan.equipment.setup_notes}</p></div>
                <div><p className="text-[10px] text-slate-500 uppercase">Ánh sáng</p><p className="text-sm text-slate-300">{plan.equipment.lighting}</p></div>
                <div><p className="text-[10px] text-slate-500 uppercase">Âm thanh</p><p className="text-sm text-slate-300">{plan.equipment.audio}</p></div>
              </div>
            )}
            {planTab === "checklist" && plan.checklist && (
              <div className="space-y-3">
                {[["before_shoot", "Trước khi quay"], ["during_shoot", "Trong khi quay"], ["after_shoot", "Sau khi quay"], ["files_to_deliver", "File bàn giao"]].map(([k, label]) => (
                  <div key={k}><p className="text-[10px] text-slate-500 uppercase">{label}</p>
                    {plan.checklist[k]?.map((c, i) => <p key={i} className="text-xs text-slate-300 mt-0.5">• {c}</p>)}
                  </div>
                ))}
              </div>
            )}
            {planTab === "editing" && plan.editing_guide && (
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(plan.editing_guide).map(([k, v]) => (
                  <div key={k} className="bg-[#0f172a] rounded-lg p-3">
                    <p className="text-[10px] text-slate-500 uppercase">{k.replace(/_/g, " ")}</p>
                    <p className="text-sm text-slate-300 mt-0.5">{v}</p>
                  </div>
                ))}
              </div>
            )}
            {planTab === "publishing" && plan.publishing && (
              <div className="space-y-3">
                <div><p className="text-[10px] text-slate-500 uppercase">Caption</p>
                  <p className="text-sm text-slate-200 bg-[#0f172a] p-3 rounded-lg">{plan.publishing.caption}</p>
                  <button onClick={() => { navigator.clipboard.writeText(plan.publishing.caption); toast.success("Copied!"); }}
                    className="text-[10px] text-lime-400 mt-1 flex items-center gap-1"><Copy className="w-3 h-3" /> Copy</button>
                </div>
                <div><p className="text-[10px] text-slate-500 uppercase">Hashtags</p>
                  <div className="flex flex-wrap gap-1 mt-1">{plan.publishing.hashtags?.map((h, i) => <Badge key={i} className="bg-sky-500/20 text-sky-400 text-[10px] border-0">{h}</Badge>)}</div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><p className="text-[10px] text-slate-500 uppercase">Giờ đăng</p><p className="text-sm text-lime-400">{plan.publishing.best_posting_time}</p></div>
                  <div><p className="text-[10px] text-slate-500 uppercase">Thumbnail</p><p className="text-sm text-slate-300">{plan.publishing.thumbnail_idea}</p></div>
                </div>
                <div><p className="text-[10px] text-slate-500 uppercase">Tips</p><p className="text-sm text-slate-300">{plan.publishing.platform_tips}</p></div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ==================== EQUIPMENT ====================
function EquipmentTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", type: "camera", status: "available", quantity: 1, notes: "" });

  const fetch = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/marketing/equipment"); setItems(data.equipment || []); }
    catch { toast.error("Lỗi tải thiết bị"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const addEquipment = async () => {
    if (!form.name.trim()) return;
    try { await api.post("/marketing/equipment", form); toast.success("Đã thêm"); setShowAdd(false); setForm({ name: "", type: "camera", status: "available", quantity: 1, notes: "" }); fetch(); }
    catch { toast.error("Lỗi"); }
  };
  const del = async (id) => { try { await api.delete(`/marketing/equipment/${id}`); fetch(); } catch {} };

  const STATUS_COLORS = { available: "bg-lime-500/20 text-lime-400", unavailable: "bg-red-500/20 text-red-400", maintenance: "bg-amber-500/20 text-amber-400" };
  const STATUS_LABELS = { available: "Sẵn sàng", unavailable: "Không khả dụng", maintenance: "Bảo trì" };

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto space-y-4" data-testid="equipment-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-200 flex items-center gap-2"><Camera className="w-5 h-5 text-lime-400" /> Thiết bị quay</h2>
        <Button onClick={() => setShowAdd(true)} size="sm" className="bg-lime-500 hover:bg-lime-400 text-[#0f172a] gap-1 text-xs" data-testid="add-equipment-btn">
          <Plus className="w-3.5 h-3.5" /> Thêm
        </Button>
      </div>

      {loading ? <p className="text-slate-500 text-center py-8">Đang tải...</p> :
      items.length === 0 ? <p className="text-slate-500 text-center py-8">Chưa có thiết bị. Bấm "Thêm" để bắt đầu.</p> :
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map(e => (
          <div key={e.id} className="bg-[#1e293b] border border-[#334155] rounded-xl p-3">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-200">{e.name}</p>
                <p className="text-[10px] text-slate-500 mt-0.5">{e.type} • x{e.quantity}</p>
              </div>
              <button onClick={() => del(e.id)} className="text-slate-600 hover:text-red-400 p-1"><Trash2 className="w-3 h-3" /></button>
            </div>
            <div className="flex items-center justify-between mt-2">
              <Badge className={`text-[9px] border-0 ${STATUS_COLORS[e.status] || ""}`}>{STATUS_LABELS[e.status] || e.status}</Badge>
              {e.notes && <span className="text-[9px] text-slate-600 truncate max-w-[120px]">{e.notes}</span>}
            </div>
          </div>
        ))}
      </div>}

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="bg-[#1e293b] border-[#334155] text-slate-200">
          <DialogHeader><DialogTitle className="text-lime-400">Thêm thiết bị</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-slate-400 text-xs">Tên thiết bị</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="bg-[#0f172a] border-[#334155] text-slate-200 mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-slate-400 text-xs">Loại</Label>
                <select value={form.type} onChange={e => setForm({...form, type: e.target.value})} className="w-full mt-1 bg-[#0f172a] border border-[#334155] rounded-md px-3 py-2 text-sm text-slate-200">
                  <option value="camera">Camera</option><option value="audio">Audio</option><option value="lighting">Lighting</option><option value="stabilizer">Stabilizer</option><option value="other">Khác</option>
                </select>
              </div>
              <div><Label className="text-slate-400 text-xs">Số lượng</Label><Input type="number" value={form.quantity} onChange={e => setForm({...form, quantity: +e.target.value})} className="bg-[#0f172a] border-[#334155] text-slate-200 mt-1" /></div>
            </div>
            <div><Label className="text-slate-400 text-xs">Ghi chú</Label><Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="bg-[#0f172a] border-[#334155] text-slate-200 mt-1" /></div>
            <Button onClick={addEquipment} className="w-full bg-lime-500 hover:bg-lime-400 text-[#0f172a] font-semibold">Thêm thiết bị</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ==================== SCRIPTS ====================
function ScriptsTab() {
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    api.get("/marketing/scripts").then(r => { setScripts(r.data.scripts || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const loadDetail = async (id) => {
    try { const { data } = await api.get(`/marketing/scripts/${id}`); setDetail(data); }
    catch { toast.error("Lỗi"); }
  };

  const STATUS_COLORS = { draft: "bg-slate-500/20 text-slate-400", used: "bg-lime-500/20 text-lime-400", archived: "bg-red-500/20 text-red-400" };

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto space-y-4" data-testid="scripts-tab">
      <h2 className="text-lg font-bold text-slate-200 flex items-center gap-2"><FileText className="w-5 h-5 text-lime-400" /> Thư viện kịch bản</h2>

      {loading ? <p className="text-slate-500 text-center py-8">Đang tải...</p> :
      scripts.length === 0 ? <p className="text-slate-500 text-center py-8">Chưa có kịch bản. Tạo từ Video Planner.</p> :
      <div className="space-y-2">
        {scripts.map(s => (
          <div key={s.id} onClick={() => loadDetail(s.id)}
            className="bg-[#1e293b] border border-[#334155] rounded-xl p-3 cursor-pointer hover:border-lime-500/30 transition-colors flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-200">{s.title}</p>
              <div className="flex items-center gap-2 mt-1">
                <Badge className="bg-sky-500/20 text-sky-400 text-[9px] border-0">{s.platform}</Badge>
                <Badge className="bg-amber-500/20 text-amber-400 text-[9px] border-0">{s.duration}</Badge>
                <Badge className={`text-[9px] border-0 ${STATUS_COLORS[s.status] || ""}`}>{s.status}</Badge>
              </div>
            </div>
            <Eye className="w-4 h-4 text-slate-500" />
          </div>
        ))}
      </div>}

      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="bg-[#1e293b] border-[#334155] text-slate-200 max-w-4xl max-h-[80vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader><DialogTitle className="text-lime-400">{detail.title}</DialogTitle></DialogHeader>
              <pre className="text-xs text-slate-300 whitespace-pre-wrap bg-[#0f172a] p-4 rounded-lg mt-2 font-sans leading-relaxed max-h-[60vh] overflow-y-auto">
                {JSON.stringify(detail.plan, null, 2)}
              </pre>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ==================== CALENDAR ====================
function CalendarTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("kanban");

  useEffect(() => {
    api.get("/marketing/calendar").then(r => { setItems(r.data.items || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const STATUSES = ["idea", "planned", "shooting", "editing", "scheduled", "posted", "completed"];
  const STATUS_LABELS = { idea: "Ý tưởng", planned: "Lên kế hoạch", shooting: "Đang quay", editing: "Đang dựng", scheduled: "Đã lên lịch", posted: "Đã đăng", completed: "Hoàn thành" };
  const STATUS_COLORS = { idea: "border-slate-500", planned: "border-sky-500", shooting: "border-amber-500", editing: "border-purple-500", scheduled: "border-lime-500", posted: "border-emerald-500", completed: "border-teal-500" };

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="calendar-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-200 flex items-center gap-2"><CalendarDays className="w-5 h-5 text-lime-400" /> Lịch content</h2>
        <div className="flex gap-1">
          <button onClick={() => setView("kanban")} className={`px-3 py-1 rounded text-[10px] ${view === "kanban" ? "bg-lime-500/20 text-lime-400" : "text-slate-500"}`}>Kanban</button>
          <button onClick={() => setView("list")} className={`px-3 py-1 rounded text-[10px] ${view === "list" ? "bg-lime-500/20 text-lime-400" : "text-slate-500"}`}>Danh sách</button>
        </div>
      </div>

      {loading ? <p className="text-slate-500 text-center py-8">Đang tải...</p> :
      items.length === 0 ? <p className="text-slate-500 text-center py-8">Lịch trống. Tạo content từ Video Planner hoặc thêm thủ công.</p> :
      view === "kanban" ? (
        <div className="flex gap-3 overflow-x-auto pb-4">
          {STATUSES.map(status => {
            const cols = items.filter(i => i.status === status);
            return (
              <div key={status} className="min-w-[200px] flex-shrink-0">
                <div className={`border-t-2 ${STATUS_COLORS[status]} bg-[#1e293b] rounded-lg p-2`}>
                  <p className="text-[10px] font-medium text-slate-400 uppercase mb-2">{STATUS_LABELS[status]} ({cols.length})</p>
                  <div className="space-y-1.5">
                    {cols.map(i => (
                      <div key={i.id} className="bg-[#0f172a] rounded-lg p-2">
                        <p className="text-xs text-slate-200 font-medium">{i.title}</p>
                        <p className="text-[9px] text-slate-500 mt-0.5">{i.date} • {i.platform}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="space-y-1">
          {items.map(i => (
            <div key={i.id} className="bg-[#1e293b] border border-[#334155] rounded-lg p-2 flex items-center gap-3">
              <span className="text-xs text-slate-500 w-20">{i.date}</span>
              <span className="text-xs text-slate-200 flex-1">{i.title}</span>
              <Badge className="text-[9px] border-0 bg-sky-500/20 text-sky-400">{i.platform}</Badge>
              <Badge className="text-[9px] border-0 bg-slate-500/20 text-slate-400">{STATUS_LABELS[i.status]}</Badge>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ==================== CAMPAIGNS ====================
function CampaignsTab() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/marketing/generate-campaign");
      setCampaigns(data.campaigns || []);
      toast.success("Đã tạo chiến dịch!");
    } catch (err) { toast.error(err.response?.data?.detail || "Lỗi"); }
    finally { setLoading(false); }
  };

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto space-y-4" data-testid="campaigns-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-200 flex items-center gap-2"><Megaphone className="w-5 h-5 text-lime-400" /> AI Campaign Generator</h2>
        <Button onClick={generate} disabled={loading} className="bg-lime-500 hover:bg-lime-400 text-[#0f172a] gap-1 text-xs" data-testid="generate-campaigns-btn">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Tạo chiến dịch
        </Button>
      </div>

      {campaigns.length === 0 ? (
        <div className="text-center py-12">
          <Megaphone className="w-10 h-10 text-slate-600 mx-auto mb-2" />
          <p className="text-sm text-slate-500">Bấm "Tạo chiến dịch" để AI phân tích data và đề xuất campaigns.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {campaigns.map((c, i) => (
            <div key={i} className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
              <div className="flex items-start justify-between mb-2">
                <h3 className="text-sm font-bold text-slate-200">{c.title}</h3>
                <Badge className={`text-[9px] border-0 ${c.priority === "high" ? "bg-red-500/20 text-red-400" : "bg-amber-500/20 text-amber-400"}`}>{c.priority}</Badge>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><span className="text-slate-500">Đối tượng:</span> <span className="text-slate-300">{c.target_segment}</span></div>
                <div><span className="text-slate-500">Kênh:</span> <span className="text-sky-400">{c.channel}</span></div>
                <div><span className="text-slate-500">Ưu đãi:</span> <span className="text-lime-400">{c.offer}</span></div>
                <div><span className="text-slate-500">Tác động:</span> <span className="text-amber-400">{c.expected_impact}</span></div>
              </div>
              {c.message && <p className="text-xs text-slate-400 mt-2 italic">"{c.message}"</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ==================== CONTENT IDEAS ====================
function ContentIdeasTab() {
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [weekInfo, setWeekInfo] = useState("");

  const fetch = useCallback(async (force = false) => {
    if (force) setRefreshing(true); else setLoading(true);
    try {
      const endpoint = force ? "/marketing/content-ideas/refresh" : "/marketing/content-ideas";
      const method = force ? "post" : "get";
      const { data } = await api[method](endpoint);
      setIdeas(data.ideas || []);
      setWeekInfo(data.week || "");
    } catch (err) { toast.error(err.response?.data?.detail || "Lỗi tải ý tưởng"); }
    finally { setLoading(false); setRefreshing(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const pushToCalendar = async (idx) => {
    try {
      const { data } = await api.post(`/marketing/content-ideas/${idx}/to-calendar`);
      toast.success(data.message);
    } catch (err) { toast.error(err.response?.data?.detail || "Lỗi"); }
  };

  const AREA_COLORS = { pickleball: "bg-lime-500/20 text-lime-400", gym: "bg-purple-500/20 text-purple-400", cafe: "bg-amber-500/20 text-amber-400", shop: "bg-sky-500/20 text-sky-400", brand: "bg-rose-500/20 text-rose-400" };

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto space-y-4" data-testid="content-ideas-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-slate-200 flex items-center gap-2"><Lightbulb className="w-5 h-5 text-lime-400" /> AI Content Ideas</h2>
          <p className="text-xs text-slate-500 mt-0.5">7 ý tưởng content hàng tuần dựa trên data kinh doanh • {weekInfo}</p>
        </div>
        <Button onClick={() => fetch(true)} disabled={refreshing} variant="outline" size="sm" className="border-[#334155] text-slate-300 hover:text-lime-400 gap-1.5 text-xs" data-testid="refresh-ideas-btn">
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} /> {refreshing ? "Đang tạo..." : "Làm mới"}
        </Button>
      </div>

      {loading ? (
        <div className="flex flex-col items-center py-12"><Loader2 className="w-6 h-6 text-lime-400 animate-spin mb-2" /><p className="text-xs text-slate-500">AI đang phân tích data...</p></div>
      ) : ideas.length === 0 ? (
        <p className="text-slate-500 text-center py-12">Bấm "Làm mới" để AI tạo ý tưởng content tuần này.</p>
      ) : (
        <div className="space-y-2">
          {ideas.map((idea, i) => (
            <div key={i} className="bg-[#1e293b] border border-[#334155] rounded-xl p-4 hover:border-[#475569]/50 transition-colors" data-testid={`idea-card-${i}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs text-lime-400 font-mono font-bold">{idea.day}</span>
                    <h3 className="text-sm font-medium text-slate-200">{idea.title}</h3>
                  </div>
                  <p className="text-xs text-slate-400 mb-2">{idea.description}</p>
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    <Badge className={`text-[9px] border-0 ${AREA_COLORS[idea.area] || "bg-slate-500/20 text-slate-400"}`}>{idea.area}</Badge>
                    <Badge className="bg-sky-500/20 text-sky-400 text-[9px] border-0">{idea.platform}</Badge>
                    <Badge className="bg-slate-500/20 text-slate-400 text-[9px] border-0">{idea.type}</Badge>
                    <Badge className={`text-[9px] border-0 ${idea.effort === "low" ? "bg-lime-500/20 text-lime-400" : idea.effort === "high" ? "bg-red-500/20 text-red-400" : "bg-amber-500/20 text-amber-400"}`}>{idea.effort}</Badge>
                  </div>
                  {idea.hook_suggestion && <p className="text-[10px] text-sky-400 italic">Hook: "{idea.hook_suggestion}"</p>}
                  {idea.business_reason && <p className="text-[10px] text-slate-600 mt-0.5">Lý do: {idea.business_reason}</p>}
                </div>
                <Button onClick={() => pushToCalendar(i)} size="sm" className="bg-lime-500/10 text-lime-400 hover:bg-lime-500/20 border border-lime-500/20 h-7 text-[10px] gap-1 shrink-0" data-testid={`push-idea-${i}`}>
                  <CalendarDays className="w-3 h-3" /> Lên lịch
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ==================== PRODUCTION TASKS ====================
function ProductionTasksTab() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [filter, setFilter] = useState("");
  const [form, setForm] = useState({ title: "", description: "", department: "media", assigned_to: "", priority: "medium", deadline: "" });

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (filter) p.set("status", filter);
      const { data } = await api.get(`/marketing/tasks?${p}`);
      setTasks(data.tasks || []);
    } catch { toast.error("Lỗi tải tasks"); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { fetch(); }, [fetch]);

  const addTask = async () => {
    if (!form.title.trim()) return;
    try { await api.post("/marketing/tasks", form); toast.success("Đã tạo task"); setShowAdd(false); setForm({ title: "", description: "", department: "media", assigned_to: "", priority: "medium", deadline: "" }); fetch(); }
    catch { toast.error("Lỗi"); }
  };

  const updateStatus = async (id, status) => {
    try { await api.put(`/marketing/tasks/${id}`, { status }); fetch(); }
    catch { toast.error("Lỗi"); }
  };

  const del = async (id) => { try { await api.delete(`/marketing/tasks/${id}`); fetch(); } catch {} };

  const PRIORITY_C = { high: "bg-red-500/20 text-red-400", medium: "bg-amber-500/20 text-amber-400", low: "bg-sky-500/20 text-sky-400" };
  const STATUS_C = { todo: "bg-slate-500/20 text-slate-400", in_progress: "bg-amber-500/20 text-amber-400", done: "bg-lime-500/20 text-lime-400", cancelled: "bg-red-500/20 text-red-400" };
  const STATUS_L = { todo: "Chưa làm", in_progress: "Đang làm", done: "Hoàn thành", cancelled: "Huỷ" };

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto space-y-4" data-testid="tasks-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-200 flex items-center gap-2"><ClipboardList className="w-5 h-5 text-lime-400" /> Production Tasks</h2>
        <div className="flex gap-2">
          <div className="flex gap-1">
            {["", "todo", "in_progress", "done"].map(s => (
              <button key={s} onClick={() => setFilter(s)} className={`px-2 py-1 rounded text-[10px] ${filter === s ? "bg-lime-500/20 text-lime-400" : "text-slate-500"}`}>
                {s ? STATUS_L[s] : "Tất cả"}
              </button>
            ))}
          </div>
          <Button onClick={() => setShowAdd(true)} size="sm" className="bg-lime-500 hover:bg-lime-400 text-[#0f172a] gap-1 text-xs" data-testid="add-task-btn">
            <Plus className="w-3.5 h-3.5" /> Tạo task
          </Button>
        </div>
      </div>

      {loading ? <p className="text-slate-500 text-center py-8">Đang tải...</p> :
      tasks.length === 0 ? <p className="text-slate-500 text-center py-8">Chưa có task nào.</p> :
      <div className="space-y-2">
        {tasks.map(t => (
          <div key={t.id} className="bg-[#1e293b] border border-[#334155] rounded-xl p-3" data-testid={`task-${t.id}`}>
            <div className="flex items-start justify-between mb-2">
              <div>
                <p className="text-sm font-medium text-slate-200">{t.title}</p>
                {t.description && <p className="text-xs text-slate-400 mt-0.5">{t.description}</p>}
              </div>
              <div className="flex gap-1">
                <Badge className={`text-[9px] border-0 ${PRIORITY_C[t.priority] || ""}`}>{t.priority}</Badge>
                <Badge className={`text-[9px] border-0 ${STATUS_C[t.status] || ""}`}>{STATUS_L[t.status] || t.status}</Badge>
              </div>
            </div>
            <div className="flex items-center gap-3 text-[10px] text-slate-600 mb-2">
              {t.assigned_to && <span>Giao: <span className="text-slate-400">{t.assigned_to}</span></span>}
              {t.deadline && <span>Deadline: <span className="text-red-400">{t.deadline}</span></span>}
              <span>{t.department}</span>
            </div>
            {/* Proof images */}
            {t.proof_images && t.proof_images.length > 0 && (
              <div className="flex gap-1 mb-2">
                {t.proof_images.map((img, i) => (
                  <a key={i} href={img} target="_blank" rel="noreferrer" className="w-12 h-12 rounded bg-[#0f172a] border border-[#334155] flex items-center justify-center">
                    <Image className="w-4 h-4 text-slate-500" />
                  </a>
                ))}
              </div>
            )}
            {/* Status actions */}
            {t.status === "todo" && (
              <div className="flex gap-1.5">
                <button onClick={() => updateStatus(t.id, "in_progress")} className="text-[10px] text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded">Bắt đầu</button>
                <button onClick={() => updateStatus(t.id, "done")} className="text-[10px] text-lime-400 bg-lime-500/10 px-2 py-0.5 rounded">Hoàn thành</button>
                <button onClick={() => del(t.id)} className="text-[10px] text-red-400 bg-red-500/10 px-2 py-0.5 rounded ml-auto">Xoá</button>
              </div>
            )}
            {t.status === "in_progress" && (
              <div className="flex gap-1.5">
                <button onClick={() => updateStatus(t.id, "done")} className="text-[10px] text-lime-400 bg-lime-500/10 px-2 py-0.5 rounded">Hoàn thành</button>
                <button onClick={() => updateStatus(t.id, "cancelled")} className="text-[10px] text-red-400 bg-red-500/10 px-2 py-0.5 rounded">Huỷ</button>
              </div>
            )}
          </div>
        ))}
      </div>}

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="bg-[#1e293b] border-[#334155] text-slate-200">
          <DialogHeader><DialogTitle className="text-lime-400">Tạo Production Task</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-slate-400 text-xs">Tiêu đề *</Label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="bg-[#0f172a] border-[#334155] text-slate-200 mt-1" /></div>
            <div><Label className="text-slate-400 text-xs">Mô tả</Label><Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="bg-[#0f172a] border-[#334155] text-slate-200 mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-slate-400 text-xs">Phòng ban</Label>
                <select value={form.department} onChange={e => setForm({...form, department: e.target.value})} className="w-full mt-1 bg-[#0f172a] border border-[#334155] rounded-md px-3 py-2 text-sm text-slate-200">
                  <option value="media">Media</option><option value="content">Content</option><option value="admin">Admin</option><option value="maintenance">Maintenance</option>
                </select>
              </div>
              <div><Label className="text-slate-400 text-xs">Ưu tiên</Label>
                <select value={form.priority} onChange={e => setForm({...form, priority: e.target.value})} className="w-full mt-1 bg-[#0f172a] border border-[#334155] rounded-md px-3 py-2 text-sm text-slate-200">
                  <option value="low">Thấp</option><option value="medium">Trung bình</option><option value="high">Cao</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-slate-400 text-xs">Giao cho</Label><Input value={form.assigned_to} onChange={e => setForm({...form, assigned_to: e.target.value})} placeholder="Tên nhân viên" className="bg-[#0f172a] border-[#334155] text-slate-200 mt-1" /></div>
              <div><Label className="text-slate-400 text-xs">Deadline</Label><Input type="date" value={form.deadline} onChange={e => setForm({...form, deadline: e.target.value})} className="bg-[#0f172a] border-[#334155] text-slate-200 mt-1" /></div>
            </div>
            <Button onClick={addTask} className="w-full bg-lime-500 hover:bg-lime-400 text-[#0f172a] font-semibold">Tạo task</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ==================== PERFORMANCE TRACKING ====================
function PerformanceTab() {
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ content_title: "", platform: "TikTok", posted_date: "", views: 0, likes: 0, comments: 0, shares: 0, leads: 0, bookings_generated: 0, revenue_generated: 0 });

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const [recRes, sumRes] = await Promise.all([api.get("/marketing/performance"), api.get("/marketing/performance/summary")]);
      setRecords(recRes.data.records || []);
      setSummary(sumRes.data);
    } catch { toast.error("Lỗi tải dữ liệu"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const addRecord = async () => {
    if (!form.content_title.trim()) return;
    try { await api.post("/marketing/performance", form); toast.success("Đã lưu"); setShowAdd(false); setForm({ content_title: "", platform: "TikTok", posted_date: "", views: 0, likes: 0, comments: 0, shares: 0, leads: 0, bookings_generated: 0, revenue_generated: 0 }); fetch(); }
    catch { toast.error("Lỗi"); }
  };

  const del = async (id) => { try { await api.delete(`/marketing/performance/${id}`); fetch(); } catch {} };

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-4" data-testid="performance-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-200 flex items-center gap-2"><BarChart3 className="w-5 h-5 text-lime-400" /> Hiệu suất Content</h2>
        <Button onClick={() => setShowAdd(true)} size="sm" className="bg-lime-500 hover:bg-lime-400 text-[#0f172a] gap-1 text-xs" data-testid="add-perf-btn">
          <Plus className="w-3.5 h-3.5" /> Thêm kết quả
        </Button>
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
          {[
            { label: "Tổng views", value: (summary.total_views || 0).toLocaleString("vi-VN"), icon: Eye, color: "text-sky-400" },
            { label: "Tổng likes", value: (summary.total_likes || 0).toLocaleString("vi-VN"), icon: Heart, color: "text-red-400" },
            { label: "Leads", value: summary.total_leads || 0, icon: Users, color: "text-amber-400" },
            { label: "Bookings", value: summary.total_bookings || 0, icon: Target, color: "text-lime-400" },
            { label: "Doanh thu", value: `${((summary.total_revenue || 0) / 1000).toFixed(0)}k`, icon: DollarSign, color: "text-emerald-400" },
            { label: "Engagement", value: `${summary.avg_engagement_rate || 0}%`, icon: TrendingUp, color: "text-purple-400" },
          ].map((m, i) => (
            <div key={i} className="bg-[#1e293b] border border-[#334155] rounded-xl p-3">
              <div className="flex items-center gap-1.5 mb-1"><m.icon className={`w-3.5 h-3.5 ${m.color}`} /><span className="text-[9px] text-slate-500 uppercase">{m.label}</span></div>
              <p className={`text-lg font-bold ${m.color}`}>{m.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Records table */}
      {loading ? <p className="text-slate-500 text-center py-8">Đang tải...</p> :
      records.length === 0 ? <p className="text-slate-500 text-center py-8">Chưa có dữ liệu hiệu suất. Thêm kết quả sau khi đăng content.</p> :
      <div className="bg-[#1e293b] border border-[#334155] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-[#0f172a]">
              <tr className="text-slate-400">
                <th className="px-3 py-2 text-left">Content</th>
                <th className="px-3 py-2 text-left">Platform</th>
                <th className="px-3 py-2 text-right">Views</th>
                <th className="px-3 py-2 text-right">Likes</th>
                <th className="px-3 py-2 text-right">Comments</th>
                <th className="px-3 py-2 text-right">Shares</th>
                <th className="px-3 py-2 text-right">Leads</th>
                <th className="px-3 py-2 text-right">Bookings</th>
                <th className="px-3 py-2 text-center w-8"></th>
              </tr>
            </thead>
            <tbody>
              {records.map(r => (
                <tr key={r.id} className="border-t border-[#334155]/30 hover:bg-[#334155]/10">
                  <td className="px-3 py-2 text-slate-200">{r.content_title}</td>
                  <td className="px-3 py-2"><Badge className="bg-sky-500/20 text-sky-400 text-[9px] border-0">{r.platform}</Badge></td>
                  <td className="px-3 py-2 text-right text-slate-300">{r.views.toLocaleString("vi-VN")}</td>
                  <td className="px-3 py-2 text-right text-red-400">{r.likes.toLocaleString("vi-VN")}</td>
                  <td className="px-3 py-2 text-right text-slate-400">{r.comments}</td>
                  <td className="px-3 py-2 text-right text-slate-400">{r.shares}</td>
                  <td className="px-3 py-2 text-right text-amber-400">{r.leads}</td>
                  <td className="px-3 py-2 text-right text-lime-400">{r.bookings_generated}</td>
                  <td className="px-3 py-2 text-center"><button onClick={() => del(r.id)} className="text-slate-600 hover:text-red-400"><Trash2 className="w-3 h-3" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>}

      {/* Add dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="bg-[#1e293b] border-[#334155] text-slate-200 max-w-lg">
          <DialogHeader><DialogTitle className="text-lime-400">Thêm kết quả Content</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-slate-400 text-xs">Tên content *</Label><Input value={form.content_title} onChange={e => setForm({...form, content_title: e.target.value})} className="bg-[#0f172a] border-[#334155] text-slate-200 mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-slate-400 text-xs">Platform</Label>
                <select value={form.platform} onChange={e => setForm({...form, platform: e.target.value})} className="w-full mt-1 bg-[#0f172a] border border-[#334155] rounded-md px-3 py-2 text-sm text-slate-200">
                  <option>TikTok</option><option>Facebook</option><option>Instagram</option><option>YouTube</option>
                </select>
              </div>
              <div><Label className="text-slate-400 text-xs">Ngày đăng</Label><Input type="date" value={form.posted_date} onChange={e => setForm({...form, posted_date: e.target.value})} className="bg-[#0f172a] border-[#334155] text-slate-200 mt-1" /></div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div><Label className="text-slate-400 text-xs">Views</Label><Input type="number" value={form.views} onChange={e => setForm({...form, views: +e.target.value})} className="bg-[#0f172a] border-[#334155] text-slate-200 mt-1" /></div>
              <div><Label className="text-slate-400 text-xs">Likes</Label><Input type="number" value={form.likes} onChange={e => setForm({...form, likes: +e.target.value})} className="bg-[#0f172a] border-[#334155] text-slate-200 mt-1" /></div>
              <div><Label className="text-slate-400 text-xs">Comments</Label><Input type="number" value={form.comments} onChange={e => setForm({...form, comments: +e.target.value})} className="bg-[#0f172a] border-[#334155] text-slate-200 mt-1" /></div>
              <div><Label className="text-slate-400 text-xs">Shares</Label><Input type="number" value={form.shares} onChange={e => setForm({...form, shares: +e.target.value})} className="bg-[#0f172a] border-[#334155] text-slate-200 mt-1" /></div>
              <div><Label className="text-slate-400 text-xs">Leads</Label><Input type="number" value={form.leads} onChange={e => setForm({...form, leads: +e.target.value})} className="bg-[#0f172a] border-[#334155] text-slate-200 mt-1" /></div>
              <div><Label className="text-slate-400 text-xs">Bookings</Label><Input type="number" value={form.bookings_generated} onChange={e => setForm({...form, bookings_generated: +e.target.value})} className="bg-[#0f172a] border-[#334155] text-slate-200 mt-1" /></div>
            </div>
            <div><Label className="text-slate-400 text-xs">Doanh thu (VND)</Label><Input type="number" value={form.revenue_generated} onChange={e => setForm({...form, revenue_generated: +e.target.value})} className="bg-[#0f172a] border-[#334155] text-slate-200 mt-1" /></div>
            <Button onClick={addRecord} className="w-full bg-lime-500 hover:bg-lime-400 text-[#0f172a] font-semibold">Lưu kết quả</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
